/**
 * Site-level data-flow transform — the testable seam for the
 * `/sites/:siteId/data-flows` view.
 *
 * Takes a `DataFlowSummary` from the API and produces three outputs
 * the React components consume directly:
 *
 *   - `tableRows` — one row per recipient vendor, aggregated across
 *     pages and chains; the recipients table renders this verbatim.
 *   - `sankeyNodes` / `sankeyLinks` — d3-sankey-shaped projections of
 *     the same graph, with pages on the left, depth-bucketed vendor
 *     columns in the middle, and recipients on the right.
 *
 * Pure transform — composes the aggregation pass
 * (`./site-data-flow-aggregation`) with the layout pass
 * (`./site-data-flow-layout`). No D3 import here so the orchestration
 * stays unit-testable without the d3-sankey bundle.
 */

import type { components } from '@trackercop/contracts';
import {
  aggregateRecipientRows,
  buildLinksWithCollapse,
  buildPageConsentModes,
  indexNodes,
  type RecipientTableRow,
  type SiteDataFlowSankeyLink,
} from './site-data-flow-aggregation';
import { computeHighlightedFlow } from './site-data-flow-highlight';
import {
  SITE_DATA_FLOW_MAX_EDGES,
  buildSankeyNodes,
  prettyPageLabel,
  type SiteDataFlowSankeyNode,
} from './site-data-flow-layout';

type DataFlowSummary = components['schemas']['DataFlowSummary'];

export {
  computeHighlightedFlow,
  prettyPageLabel,
  SITE_DATA_FLOW_MAX_EDGES,
  type RecipientTableRow,
  type SiteDataFlowSankeyLink,
  type SiteDataFlowSankeyNode,
};

export interface SiteDataFlowGraph {
  tableRows: RecipientTableRow[];
  sankeyNodes: SiteDataFlowSankeyNode[];
  sankeyLinks: SiteDataFlowSankeyLink[];
  /** Number of folded edges, surfaced so the UI can render a banner. */
  collapsedEdgeCount: number;
  /** Convenience for the empty-Sankey copy. */
  hasFlows: boolean;
}

export interface BuildSiteDataFlowGraphOpts {
  /** Override the default 200-edge cap (mostly for tests). */
  maxEdges?: number;
}

export function buildSiteDataFlowGraph(
  summary: DataFlowSummary,
  opts: BuildSiteDataFlowGraphOpts = {},
): SiteDataFlowGraph {
  const maxEdges = opts.maxEdges ?? SITE_DATA_FLOW_MAX_EDGES;
  const index = indexNodes(summary);

  const tableRows = aggregateRecipientRows(summary, index);

  const cloakedRecipientIds = new Set<string>();
  for (const r of summary.recipients) {
    if (r.vendor.cloakedVia) cloakedRecipientIds.add(r.nodeId);
  }
  const collapse = buildLinksWithCollapse(
    summary.flows,
    maxEdges,
    cloakedRecipientIds,
  );
  const referencedIds = new Set<string>();
  for (const l of collapse.links) {
    referencedIds.add(l.sourceId);
    referencedIds.add(l.targetId);
  }

  const pageConsentModes = buildPageConsentModes(summary, index);
  const sankeyNodes = buildSankeyNodes(
    summary,
    index,
    referencedIds,
    collapse.otherSourcesNode,
    pageConsentModes,
  );

  return {
    tableRows,
    sankeyNodes,
    sankeyLinks: collapse.links,
    collapsedEdgeCount: collapse.collapsedEdgeCount,
    hasFlows: collapse.links.length > 0,
  };
}
